import { createSelector } from 'reselect';

import { CachedRecordingItem } from 'store/recordings/reducer.types';
import { ReduxStore } from 'store/types';

import { isProtectedRecording } from 'utils/recordings.ts';

export const selectRecordingsStore = (state: ReduxStore) => state.recordings;

export const selectDeleteSeriesRecordingRequestStatus = (state: ReduxStore) =>
    state.recordings.deleteSeriesRequest.status;

const getDeleteRequest = createSelector(
    selectRecordingsStore,
    (recordings) => recordings.deleteRequest,
);
export const selectRecordingDeleteApiStatus = createSelector(
    getDeleteRequest,
    (request) => request.status,
);

export const selectNPVRActionsData = createSelector(
    selectRecordingsStore,
    (recordings) => recordings.nPVRActions,
);

function getCachedRecordings(state: ReduxStore) {
    return state.recordings.cache.recordings;
}

export const selectCachedRecordingsByEventId = createSelector(
    getCachedRecordings,
    (recordings) =>
        recordings.reduce<Record<string, CachedRecordingItem>>((acc, rec) => {
            return {
                ...acc,
                [rec.eventId]: rec,
            };
        }, {}),
);

export const selectCachedRecordingsBySeriesId = (
    _seriesId: string | undefined,
) =>
    createSelector(getCachedRecordings, (series) =>
        _seriesId
            ? series.filter(({ seriesId }) => _seriesId === seriesId)
            : [],
    );

export const selectSeriesRecordingsHasProtectedRecording = (
    seriesId: string | undefined,
) =>
    createSelector(getCachedRecordings, (seriesRecordings) =>
        seriesRecordings.some(
            (seriesRecording) =>
                seriesRecording.seriesId === seriesId &&
                isProtectedRecording(seriesRecording),
        ),
    );

function getCachedSeries(state: ReduxStore) {
    return state.recordings.cache.series;
}

export const selectCachedRecordingByEventId = (eventId?: string) =>
    createSelector(selectCachedRecordingsByEventId, (recordingsByEventId) =>
        eventId ? recordingsByEventId[eventId] : undefined,
    );

export const selectCachedSeriesStatus = (seriesId: string | undefined) =>
    createSelector(getCachedSeries, (series) =>
        seriesId ? series.find(({ id }) => id === seriesId)?.status : undefined,
    );

export function getRecordingsCacheSyncTime(state: ReduxStore) {
    return state.recordings.cache.timestamp;
}

export function getDeleteActionPinValidationTime(
    state: ReduxStore,
    profileId: string | undefined,
) {
    return profileId
        ? state.recordings.deleteActionPinValidationTime[profileId]
        : undefined;
}
